import type { LicensePlate, VehicleIdentityRecord } from '@navio/nsdk';
import { formatLicensePlate } from '../licensePlate/licensePlate';

// Messy but supports all car types used across Noddi
export type CarProps = {
  make?: string | null;
  model?: string | null;
  licensePlateNumber?: string | null;
  makeName?: string | null;
  /** Catalog family ("Volkswagen ID.4"). Used whole when it is non-empty. */
  displayName?: string | null;
  year?: number | null;
  licensePlate?: string | LicensePlate | { number?: string } | { [key: string]: unknown } | null;
  vehicleIdentity?: VehicleIdentityRecord;
};

export const getCarLicensePlateText = (car: CarProps): string => {
  if (car.licensePlateNumber) {
    return car.licensePlateNumber;
  }
  const lp = car.licensePlate;
  if (typeof lp === 'string') {
    return lp;
  }
  if (lp && typeof lp === 'object') {
    if (
      'personalisedNumber' in lp &&
      typeof lp.personalisedNumber === 'string' &&
      'number' in lp &&
      typeof lp.number === 'string'
    ) {
      return `${lp.personalisedNumber} (${lp.number})`;
    }
    if ('number' in lp && typeof lp.number === 'string') {
      return lp.number;
    }
  }
  return '';
};

/** A car's plate for display: always this, never the raw `licensePlateNumber`. */
export const formatCarLicensePlate = (car: CarProps) => formatLicensePlate(getCarLicensePlateText(car));

type GetCarDisplayNameOptions = {
  car: CarProps;
  showLicensePlate?: boolean;
  showYear?: boolean;
};

// Default to always show year, request from tom, opting out in customer-facing apps most places.
export const getCarDisplayName = ({ car, showLicensePlate = true, showYear = true }: GetCarDisplayNameOptions) => {
  const identity = car.vehicleIdentity;
  const familyName = car.displayName?.trim();
  const make = identity?.make ?? car.make ?? car.makeName ?? '';
  const model = identity?.model ?? car.model ?? '';
  const year = identity ? identity.modelYear : car.year;
  const name = familyName || [make, model].filter(Boolean).join(' ');
  const yearText = showYear && year ? ` (${year})` : '';
  const plate = showLicensePlate ? formatCarLicensePlate(car) : '';
  return [name + yearText, plate].filter(Boolean).join(' - ').trim();
};
